import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { LoadingCompSmall } from '../../../components/re-usable/loading/loading';
import useAuth from '../../../hooks/useAuth';
import { ImpexiumUserCreateResponse } from '../../../models/impexium/impexium-user-create.model';
import { cleanImpexiumSubscriptionsUpdate, postImpexiumSubscriptionsUpdate } from '../../../redux/slice/impexium/impexium-subscriptions-update.slice';
import { cleanImpexiumSubscriptions } from '../../../redux/slice/impexium/impexium-subscriptions.slice';
import { cleanImpexiumUserCreate, postImpexiumUserCreate } from '../../../redux/slice/impexium/impexium-user-create.slice';
import { cleanImpexiumUserInfo, retrieveImpexiumUserInfo } from '../../../redux/slice/impexium/impexium-user-info.slice';
import { RootState, useAppDispatch } from '../../../redux/store';
import { useLocation } from 'react-router-dom';

interface FormElements extends HTMLFormControlsCollection {
  txtEmailSearch: HTMLInputElement
}

interface EmailInputElement extends HTMLFormElement {
  readonly elements: FormElements
}

interface Props {
  isLoggedIn: boolean;
  isNewEmail: boolean;
  setFormValue: Function;
  setIsNewEmail: Function;
  calledUnsubscribe: Function;
}

interface SubscriptionFields {
  name: string;
  value: string;
}

const EmailInputComponent = (props: Props) => {
  const auth = useAuth();
  const user = auth.getUser();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const query = new URLSearchParams(location.search.toLowerCase());

  const Preferences = useSelector((state: RootState) => state.preferences);
  const preferenceData = Preferences.data.result;

  const { data: userInfo } = useSelector((state: RootState) => state.impexiumUserInfo);
  const { data: newUserInfo } = useSelector((state: RootState) => state.impexiumUserCreate);
  
  const { isLoggedIn, isNewEmail, setFormValue, setIsNewEmail, calledUnsubscribe } = props;
  const [disableButton, setDisableButton] = useState(true);
  const [value, setValue] = useState("");
  const [isCallingApi, setIsCallingApi] = useState(false);
  const [isCallingNewApi, setIsCallingNewApi] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [isCallingBlueButton, setIsCallingBlueButton] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [defaultSubscriptionList, setDefaultSubscriptionList] = useState<SubscriptionFields[]>([]);
  const [initialPage, setInitialPage] = useState(true);
  const [emailInQuery, setEmailInQuery] = useState(false);

  const isEmailInQuery = query.get("email");

  const formRef = useRef<HTMLFormElement>(null);

  if (isEmailInQuery !== null && !isSubmittingForm && !isCallingApi && !isCallingNewApi && initialPage) {
    setInitialPage(false);
    setValue(isEmailInQuery);
    setDisableButton(false);
    setEmailInQuery(true);

  }

  const handleSubmit = (event: React.FormEvent<EmailInputElement>) => {
    event.preventDefault();
    setIsSubmittingForm(true);

    if (isNewEmail) {
      setIsCreatingUser(true);
      setIsCallingNewApi(true);
    } else {
      setIsCallingApi(true);
    }
  };

  const onClickBlueUnsubscribe = () => {
    setIsCallingBlueButton(true);
    setDisableButton(true);
    dispatch(postImpexiumUserCreate({id: "", email: email, firstName: "None", lastName: "None", gender: ""})).then((data) => {
      let payload = data.payload as ImpexiumUserCreateResponse;

      if (payload?.member?.id) {
        dispatch(postImpexiumSubscriptionsUpdate({ userId: payload.member.id, subscriptionField: defaultSubscriptionList })).then((data) => {
          setIsCallingBlueButton(false);
          setDisableButton(false);
          setIsCallingApi(true);
        });
      }
    })
  }

  const onChange = (e: React.FormEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    setValue(newValue);

    if (newValue.length > 0)
      setDisableButton(false);
    else
      setDisableButton(true);
  }

  const onNewChange = (e: React.FormEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    const elementId = e.currentTarget.id;

    if (elementId === 'txtAddEmail') {
      setEmail(newValue);
    } else if (elementId === 'txtAddFirst') {
      setFirstName(newValue);
    } else if (elementId === 'txtAddLast') {
      setLastName(newValue);
    }
  }

  const onClick = () => {
    const retainEmail = user?.refreshToken?.userName ? user.refreshToken.userName : isEmailInQuery ? isEmailInQuery : undefined;
    setEmail("");
    setFirstName("");
    setLastName("");
    setFormValue(false);
    setIsNewEmail(false);
    setValue(retainEmail || "");
    setDisableButton(retainEmail ? false : true)
    setIsSubmittingForm(false);
    dispatch(cleanImpexiumSubscriptions())
    dispatch(cleanImpexiumSubscriptionsUpdate());
    dispatch(cleanImpexiumUserCreate());
    dispatch(cleanImpexiumUserInfo());
  }

  useEffect(() => {
    if (emailInQuery) {
      setEmailInQuery(false);
      formRef.current?.requestSubmit();
    }
  }, [emailInQuery])

  useEffect(() => {
    if (isNewEmail){
      if (email.length > 0 && firstName.length > 0 && lastName.length > 0)
        setDisableButton(false);
      else
        setDisableButton(true);
    }
  }, [email, firstName, lastName]);

  useEffect(() => {
    if (initialPage && user?.refreshToken?.userName) {
      setValue(user.refreshToken.userName);
      setDisableButton(false);
      setInitialPage(false);
      setEmailInQuery(true);
    }
    else if (user?.refreshToken?.userName) {
      setValue(user.refreshToken.userName);
      setDisableButton(false);
    }
  }, [user])

  useEffect(() => {
    if (isCallingApi && value) {
      dispatch(retrieveImpexiumUserInfo(value))
      setIsCallingApi(false);
    }
  }, [isCallingApi])

  useEffect(() => {
    if (isCallingNewApi && email && firstName && lastName) {
      setIsCallingNewApi(false);
      dispatch(postImpexiumUserCreate({id: "", email: email, firstName: firstName, lastName: lastName, gender: ""}))
    }
  }, [isCallingNewApi])

  useEffect(() => {
    if (userInfo?.result?.dataList?.length > 0) {
      let data = userInfo.result.dataList[0];
      setEmail(data.email);
      setFirstName(data.firstName);
      setLastName(data.lastName);

      setIsNewEmail(false);
      setFormValue(true);
      setIsCreatingUser(false);
    } else if (userInfo?.result?.dataList === null) {
      setEmail(value);
      setDisableButton(true);
      setIsNewEmail(true);
    }
  }, [userInfo])

  useEffect(() => {
    if (newUserInfo?.member?.id && !isCallingBlueButton) {
      setTimeout(() => {
        dispatch(retrieveImpexiumUserInfo(newUserInfo.member.email))
      }, 5000)
    }
  }, [newUserInfo])

  useEffect(() => {
    if (preferenceData?.length > 0) {
      let newSubscriptionList = [] as SubscriptionFields[];

      preferenceData.map((item) => {
        newSubscriptionList.push({ name: item.subName, value: item.subName.toLowerCase().includes("opt_out") ? "True" : "False" });
      });

      setDefaultSubscriptionList(newSubscriptionList);
    }
  }, [preferenceData]);

  return (
    <>
      {isNewEmail && (
        <>
          <button id="btnClear" type="button" className="btn btn-default" onClick={onClick} disabled={isCallingBlueButton || isCreatingUser ? true : undefined}>
            Back to Search
          </button>
          <hr />
          <p className="lead">To unsubscribe from all, use the Unsubscribe button.</p>
          {isCallingBlueButton ? (
            <><LoadingCompSmall customStyle={{ verticalAlign: "middle", marginLeft: "6em", marginBottom: "22px"}} /></>
          ) : (
            <>
              <button id="btnBlanketUnSub" type="button" className="btn btn-primary" onClick={onClickBlueUnsubscribe} disabled={isCreatingUser ? true : undefined}>
                Unsubscribe from All
              </button>
              <hr />
            </>
          )}
          <p className="lead">
            <span>To create an account with RAPS and update your preferences, please fill out the form below.</span>
          </p>
        </>
      )}
      <form className="form-horizontal" onSubmit={handleSubmit} ref={formRef}>
        {isLoggedIn && userInfo?.result?.dataList?.length > 0 ? (
          <>
            <LoggedInForm email={email} firstName={firstName} lastName={lastName} />
          </>
        ) : !isLoggedIn && isNewEmail ? (
          <>
            <NewEmailForm email={email} firstName={firstName} lastName={lastName} onChange={onNewChange} disableButton={disableButton} isCreatingUser={isCreatingUser} />
          </>
        ) : (
          <>
            <NotLoggedInForm onChange={onChange} value={value} disableButton={disableButton} isSubmittingForm={isSubmittingForm} />
          </>
        )}
      </form>
      {isLoggedIn && userInfo?.result?.dataList?.length > 0 && (
        <div>
          <div className="row">
            <div className="col-sm-6 col-sm-offset-6">
              <button id="btnClear" type="button" className="btn btn-default btn-lg pull-right" style={{ margin: "2em" }} onClick={onClick}>
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmailInputComponent;

const LoggedInForm = ({email, firstName, lastName}: {email: string, firstName: string, lastName: string}) => {
  return(<>
    <div className="form-group">
          <div className="col-sm-offset-2 col-sm-8">
            <label className="control-label" htmlFor="txtAddEmail">
              Email
            </label>
            <input type="email" id="txtAddEmail" className="form-control" readOnly={true} value={email} />
          </div>
        </div>
        <div className="form-group">
          <div className="col-sm-offset-2 col-sm-8">
            <label className="control-label" htmlFor="txtAddFirst">
              First Name
            </label>
            <input type="text" id="txtAddFirst" className="form-control" readOnly={true} value={firstName} />
          </div>
        </div>
        <div className="form-group">
          <div className="col-sm-offset-2 col-sm-8">
            <label className="control-label" htmlFor="txtAddLast">
              Last Name
            </label>
            <input type="text" id="txtAddLast" className="form-control" readOnly={true} value={lastName} />
          </div>
        </div>
  </>)
}

const NotLoggedInForm = ({onChange, value, disableButton, isSubmittingForm}: {onChange: (e: React.FormEvent<HTMLInputElement>) => void, value: string, disableButton: boolean, isSubmittingForm: boolean}) => {
  return(<>
    <div className="form-group">
          <div className="col-sm-offset-2 col-sm-4">
            <label className="control-label" htmlFor="txtEmailSearch">
              Email
            </label>
            <input className="form-control" type="email" id="txtEmailSearch" required={true} onChange={onChange} value={value} />
          </div>
        </div>
        <div className="form-group">
          <div className="col-sm-offset-2 col-sm-8">
            <div>
              {isSubmittingForm ? 
                <LoadingCompSmall customStyle={{verticalAlign: "middle", marginLeft: 50}} /> : 
                <button id="btnSearch" type="submit" className="btn btn-default" disabled={disableButton}>
                  Search
              </button>
              }
            </div>
          </div>
        </div>
  </>)
};

const NewEmailForm = ({
  email,
  firstName,
  lastName,
  disableButton,
  isCreatingUser,
  onChange,
}: {
  email: string;
  firstName: string;
  lastName: string;
  disableButton: boolean;
  isCreatingUser: boolean;
  onChange: (e: React.FormEvent<HTMLInputElement>) => void;
}) => {
  return (
    <>
      <div className="form-group">
        <div className="col-sm-offset-2 col-sm-8">
          <label className="control-label" htmlFor="txtAddEmail">
            Email
          </label>
          <input type="email" id="txtAddEmail" className="form-control" value={email} onChange={onChange} />
        </div>
      </div>
      <div className="form-group">
        <div className="col-sm-offset-2 col-sm-8">
          <label className="control-label" htmlFor="txtAddFirst">
            First Name
          </label>
          <input type="text" id="txtAddFirst" className="form-control" value={firstName} onChange={onChange} />
        </div>
      </div>
      <div className="form-group">
        <div className="col-sm-offset-2 col-sm-8">
          <label className="control-label" htmlFor="txtAddLast">
            Last Name
          </label>
          <input type="text" id="txtAddLast" className="form-control" value={lastName} onChange={onChange} />
        </div>
      </div>
      <div className="form-group">
        <div className="col-sm-offset-2 col-sm-8">
          {isCreatingUser ? (
            <LoadingCompSmall customStyle={{ verticalAlign: "middle", marginLeft: "6em" }} />
          ) : (
            <>
              <button id="btnAdd" type="submit" className="btn btn-success" disabled={disableButton}>
                Update Preferences
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};